import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAllRewards, getAllUserRewards } from "../api/rewardsAPI";
import { Bounce, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Gift, Award, RefreshCw, Loader2, Search, User } from "lucide-react";
import config from "../config";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const getUserIdentifier = (user) => {
  if (!user) return null;
  return (user.id || user._id || user.user_id)?.toString();
};

const rewardImageSrc = (reward) => {
  if (!reward) return null;
  if (reward.photo && reward.photo.startsWith("http")) return reward.photo;
  if (reward.photo) return `${config.apiUrl}/assets/uploads/${reward.photo}`;
  return null;
};

export default function MyVigor() {
  const { userData } = useAuth();
  const { t } = useTranslation();

  const [availableRewards, setAvailableRewards] = useState([]);
  const [myRewards, setMyRewards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const currentUserId = useMemo(() => getUserIdentifier(userData), [userData]);

  const fetchData = useCallback(async () => {
    if (!currentUserId) return;

    setIsLoading(true);
    try {
      const [allRewards, allUserRewardRecords] = await Promise.all([
        getAllRewards(),
        getAllUserRewards(),
      ]);

      const now = new Date();
      const activeRewards = Array.isArray(allRewards)
        ? allRewards.filter((reward) => {
            const isActive = reward.is_active ?? true;
            const notExpired = !reward.end_date || new Date(reward.end_date) > now;
            return isActive && notExpired;
          })
        : [];

      const filteredMyRewards = Array.isArray(allUserRewardRecords)
        ? allUserRewardRecords.filter((record) => {
            const user = record.user || {};
            const recordUserId = getUserIdentifier(user) || (record.user_id || record.userId || record.user?.id || record.user?._id)?.toString();
            return recordUserId === currentUserId;
          })
        : [];

      setAvailableRewards(activeRewards);
      setMyRewards(filteredMyRewards);
    } catch (error) {
      console.error("MyVigor load error:", error);
      toast.error(
        t("myVigorLoadError", { defaultValue: "Unable to load your VIGOR rewards." }),
        { transition: Bounce }
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredMyRewards = myRewards.filter((record) => {
    const query = searchText.trim().toLowerCase();
    if (!query) return true;

    const reward = record.reward || {};
    const partner = record.partner || {};
    const user = record.user || {};
    return [
      reward.name,
      reward.title,
      partner.full_name,
      partner.user_name,
      user.full_name,
      user.user_mobile,
      record.id,
      record._id,
    ]
      .filter(Boolean)
      .some((field) => field.toString().toLowerCase().includes(query));
  });

  const redeemedCount = myRewards.filter((record) => record.is_redeemed).length;
  const pendingCount = myRewards.length - redeemedCount;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t("myVigor", { defaultValue: "My VIGOR" })}</h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-500">
              {t(
                "myVigorSubtitle",
                { defaultValue: "Your personal profile, current balance, and reward activity." }
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-orange-300"
            disabled={isLoading}
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
            {t("refresh", { defaultValue: "Refresh" })}
          </button>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          <div className="rounded-3xl border border-gray-200 bg-slate-50 p-6 shadow-sm">
            <div className="flex items-center gap-3 text-2xl text-orange-500">
              <User size={28} />
              <div>
                <p className="text-sm uppercase tracking-wide text-gray-500">{t("personalData", { defaultValue: "Personal Data" })}</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{userData?.full_name || userData?.user_name || t("guest", { defaultValue: "Guest" })}</p>
              </div>
            </div>
            <div className="mt-6 space-y-3 text-sm text-gray-700">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{t("email", { defaultValue: "Email" })}</p>
                <p>{userData?.user_email || userData?.email || "-"}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{t("phone", { defaultValue: "Phone" })}</p>
                <p>{userData?.user_mobile || userData?.phone || "-"}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">{t("userType", { defaultValue: "User Type" })}</p>
                <p>{
                  userData?.user_type === 1 ? t("admin", { defaultValue: "Admin" }) :
                  userData?.user_type === 3 ? t("mediator", { defaultValue: "Mediator" }) :
                  userData?.user_type === 4 ? t("partner", { defaultValue: "Partner" }) :
                  t("normal", { defaultValue: "Normal" })
                }</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <p className="text-sm uppercase tracking-wide text-gray-500">{t("currentBalance", { defaultValue: "Current Balance" })}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-slate-50 p-5 text-center">
                <p className="text-sm text-gray-500">{t("coins", { defaultValue: "Coins" })}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">{userData?.vigor_coins ?? 0}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-5 text-center">
                <p className="text-sm text-gray-500">{t("points", { defaultValue: "Points" })}</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">{userData?.vigor_points ?? 0}</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3 text-2xl text-orange-500">
              <Gift size={28} />
              <div>
                <p className="text-sm uppercase tracking-wide text-gray-500">{t("rewardSummary", { defaultValue: "Reward Summary" })}</p>
                <p className="mt-2 text-lg font-semibold text-gray-900">{myRewards.length} {t("myRewards", { defaultValue: "My Rewards" })}</p>
              </div>
            </div>
            <div className="mt-6 space-y-3 text-sm text-gray-700">
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                <span>{t("redeemed", { defaultValue: "Redeemed" })}</span>
                <span className="font-semibold text-gray-900">{redeemedCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                <span>{t("notRedeemed", { defaultValue: "Not Redeemed" })}</span>
                <span className="font-semibold text-gray-900">{pendingCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
                <span>{t("availableRewards", { defaultValue: "Available Rewards" })}</span>
                <span className="font-semibold text-gray-900">{availableRewards.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mb-8 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Award size={22} />
            {t("availableRewards", { defaultValue: "Available Rewards" })}
          </div>
          <div className="text-sm text-gray-500">{availableRewards.length} {t("activeRewards", { defaultValue: "active rewards" })}</div>
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            <Loader2 size={28} className="mx-auto mb-3 animate-spin" />
            {t("loadingAvailableRewards", { defaultValue: "Loading available rewards..." })}
          </div>
        ) : availableRewards.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            {t("noAvailableRewards", { defaultValue: "No available rewards at the moment." })}
          </div>
        ) : (
          <div className="grid gap-5 xl:grid-cols-2">
            {availableRewards.map((reward) => {
              const imageSrc = rewardImageSrc(reward);
              const isActive = reward.is_active ?? true;
              return (
                <div key={reward.id || reward._id} className="overflow-hidden rounded-3xl border border-gray-200 bg-slate-50 shadow-sm">
                  {imageSrc ? (
                    <img
                      className="h-48 w-full object-cover"
                      src={imageSrc}
                      alt={reward.name || reward.title || "reward image"}
                      onError={(e) => { e.target.onerror = null; e.target.src = "/placeholder.png"; }}
                    />
                  ) : (
                    <div className="flex h-48 items-center justify-center bg-gray-100 text-sm text-gray-500">
                      {t("noImageAvailable", { defaultValue: "No image available" })}
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">{reward.name || reward.title || "Untitled reward"}</h3>
                        <p className="mt-2 text-sm leading-6 text-gray-600">{reward.description || t("noDescription", { defaultValue: "No description available." })}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isActive ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {isActive ? t("active", { defaultValue: "Active" }) : t("inactive", { defaultValue: "Inactive" })}
                      </span>
                    </div>

                    <div className="mt-5 grid gap-3 text-sm text-gray-700 sm:grid-cols-2">
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{t("partner", { defaultValue: "Partner" })}</p>
                        <p className="mt-1 font-semibold text-gray-900">{reward.partner?.full_name || reward.partner_name || "-"}</p>
                      </div>
                      <div className="rounded-2xl bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase tracking-wide text-gray-500">{t("validity", { defaultValue: "Validity" })}</p>
                        <p className="mt-1 font-semibold text-gray-900">{formatDate(reward.starting_date)} - {formatDate(reward.end_date)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Gift size={22} />
            {t("myRewards", { defaultValue: "My Rewards" })}
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            <div className="text-sm text-gray-500">
              {filteredMyRewards.length} / {myRewards.length} {t("records", { defaultValue: "records" })}
            </div>
            <label className="relative block w-full max-w-md">
              <span className="sr-only">{t("search", { defaultValue: "Search" })}</span>
              <span className="pointer-events-none absolute inset-y-0 left-3 inline-flex items-center text-gray-400">
                <Search size={16} />
              </span>
              <input
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t("searchMyRewards", { defaultValue: "Search by reward, partner, or ID" })}
                className="w-full rounded-full border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
              />
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            <Loader2 size={28} className="mx-auto mb-3 animate-spin" />
            {t("loadingMyRewards", { defaultValue: "Loading your rewards..." })}
          </div>
        ) : myRewards.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-gray-500">
            {t("noMyRewards", { defaultValue: "You have no rewards yet." })}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">{t("reward", { defaultValue: "Reward" })}</th>
                  <th className="px-4 py-3">{t("partner", { defaultValue: "Partner" })}</th>
                  <th className="px-4 py-3">{t("status", { defaultValue: "Status" })}</th>
                  <th className="px-4 py-3">{t("redeemedDate", { defaultValue: "Redeemed Date" })}</th>
                  <th className="px-4 py-3">{t("createdAt", { defaultValue: "Created At" })}</th>
                  <th className="px-4 py-3">{t("actions", { defaultValue: "Actions" })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredMyRewards.map((record) => {
                  const reward = record.reward || {};
                  const partner = record.partner || {};
                  const status = record.is_redeemed ? t("redeemed", { defaultValue: "Redeemed" }) : t("pending", { defaultValue: "Pending" });
                  return (
                    <tr key={record.id || record._id}>
                      <td className="px-4 py-4 align-top">
                        <div className="font-semibold text-gray-900">{reward.name || reward.title || t("unknownReward", { defaultValue: "Unknown reward" })}</div>
                        <div className="text-xs text-gray-500">{reward.description ? reward.description.slice(0, 90) : t("noDescription", { defaultValue: "No description" })}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">{reward.partner_name || partner.user_name || t("unknownPartner", { defaultValue: "Unknown partner" })}</td>
                      <td className="px-4 py-4 align-top">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${record.is_redeemed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-700">{formatDate(record.redeemed_date || record.redeemedDate)}</td>
                      <td className="px-4 py-4 align-top text-gray-700">{formatDate(record.createdAt || record.created_at || record.created_date)}</td>
                      <td className="px-4 py-4 align-top text-sm text-orange-600">{record.is_redeemed ? t("view", { defaultValue: "View" }) : t("pending", { defaultValue: "Pending" })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
